import * as THREE from 'three';
import { ball, box, croissant, cup, leaf, material, mesh, planter, random, texture, tube } from './art';
import { FINDS, PLACES } from './data';
import { addGallery, DEFAULT_SEED, inWorld, nearPainting, paintingHeight, Outskirts, REGIONS } from './expansion';
import { CAT_OBSTACLES, makeSleepingCat } from './cat';
import { LeafBridge } from './leafBridge';
import { FriendlyInsects, LOTUS_LANDINGS } from './friendlyInsects';
import { SAVE_STATIONS } from './saveStations';
import { Picnic, picnicReserved } from './picnic';
import { GardenCorners, cornersReserved } from './gardenCorners';
import { EveningCafe, eveningReserved } from './eveningCafe';
import { Frontier, onCanalBridge } from './frontier';

export interface Obstacle { x: number; z: number; radius: number; }
export class World {
  readonly group = new THREE.Group();
  readonly obstacles: Obstacle[] = [];
  readonly pickups = new Map<string, THREE.Group>();
  readonly beacons = new Map<string, THREE.Group>();
  readonly water: THREE.Mesh[] = [];
  readonly butterfly = new THREE.Group();
  private readonly details = new Map<string, THREE.Group>();
  private lastChunk = '';
  readonly outskirts: Outskirts;
  readonly cat: ReturnType<typeof makeSleepingCat>;
  readonly bridge: LeafBridge;
  readonly friends: FriendlyInsects;
  readonly picnic: Picnic;
  readonly corners: GardenCorners;
  readonly evening: EveningCafe;
  readonly frontier: Frontier;
  private readonly grassGeometry: THREE.BufferGeometry;
  private readonly grassMaterial = new THREE.MeshStandardMaterial({ color: '#789757', side: THREE.DoubleSide, roughness: .85 });
  private readonly pebbleGeometry = new THREE.DodecahedronGeometry(1, 0);
  private readonly pebbleMaterial = material('#a4a591');

  constructor(scene: THREE.Scene, seed = DEFAULT_SEED, bridgeSteps = 0, berrySteps = 0, fountainCalm = false) {
    scene.add(this.group);
    this.outskirts = new Outskirts(this.group, seed);
    this.bridge = new LeafBridge(this.group, bridgeSteps);
    this.picnic = new Picnic(this.group, berrySteps);
    this.corners = new GardenCorners(this.group, fountainCalm);
    this.evening = new EveningCafe(this.group);
    this.frontier = new Frontier(this.group, seed);
    const shape = new THREE.Shape(); shape.moveTo(-.14, 0); shape.quadraticCurveTo(-.08, 1.7, .65, 2.8); shape.quadraticCurveTo(.1, 1.15, .14, 0); shape.closePath();
    this.grassGeometry = new THREE.ShapeGeometry(shape, 3);
    this.ground(); this.cafe(); this.napkin(); this.lake(); this.garden(); this.bench(); this.louvre(); this.collectibles(); this.markers();
    this.obstacles.push(...addGallery(this.group));
    this.cat = makeSleepingCat(this.group);
    this.obstacles.push(...CAT_OBSTACLES);
    planter(this.group, 33, -29, .72); this.obstacles.push({ x: 33, z: -29, radius: 4.2 });
    this.friends = new FriendlyInsects(this.group, seed);
    for (const sign of [-1, 1]) { const wing = ball(this.butterfly, sign * .28, 0, 0, .3, .035, .35, '#f3c35e'); wing.rotation.z = sign * .35; }
    ball(this.butterfly, 0, 0, 0, .04, .05, .25, '#5f4836'); this.group.add(this.butterfly);
  }

  private ground(): void {
    const stone = texture('stone'); stone.repeat.set(36, 20); stone.anisotropy = 4;
    const floor = mesh(new THREE.PlaneGeometry(300, 170), new THREE.MeshStandardMaterial({ map: stone, roughness: 1 }), this.group); floor.rotation.x = -Math.PI / 2; floor.castShadow = false;
    const soil = texture('soil'); soil.repeat.set(12, 4);
    const bed = mesh(new THREE.PlaneGeometry(290, 30), new THREE.MeshStandardMaterial({ map: soil }), this.group, 0, .012, -68); bed.rotation.x = -Math.PI / 2; bed.castShadow = false;
    box(this.group, 0, .4, -52, 300, .8, 1, '#b4b7a0');
    const r = random(9);
    // Tall borders frame the playable strip without blocking its route.
    for (let i = 0; i < 24; i++) {
      const x = -140 + i * 12;
      ball(this.group, x, 2.7, -68 - r() * 6, 8, 4 + r() * 3, 6, i % 2 ? '#527756' : '#658358');
    }
    for (let i = 0; i < 11; i++) {
      const x = -137 + i * 26;
      const l = leaf(this.group, x, .08, 70 + r() * 6, 7, 2, '#9a9a51', r() * 6); l.rotation.z = .08;
    }
  }

  private cafe(): void {
    cup(this.group, -112, 39); this.obstacles.push({ x: -112, z: 39, radius: 5.1 });
    croissant(this.group, -96, 39); this.obstacles.push({ x: -96, z: 42, radius: 3.6 });
    const table = new THREE.Group(); this.group.add(table);
    for (const [x, z] of [[-123, 29], [-123, 60], [-93, 15], [-93, 60]]) {
      mesh(new THREE.CylinderGeometry(1, 1.5, 31, 12), '#42645c', table, x, 15.5, z);
      mesh(new THREE.CylinderGeometry(2.2, 2.5, .8, 12), '#35594f', table, x, .4, z);
      this.obstacles.push({ x, z, radius: 2.3 });
    }
    const tabletop = mesh(new THREE.CylinderGeometry(30, 30, 1.3, 48), '#dedcce', table, -109, 31.5, 37);
    tabletop.layers.set(1); // Elevated surfaces support targeted fading in the exploration camera.
    tabletop.castShadow = false;
    const rim = mesh(new THREE.TorusGeometry(30, .3, 6, 48), '#506d65', table, -109, 32, 37); rim.rotation.x = Math.PI / 2; rim.layers.set(1); rim.castShadow = false;
    for (const [x, z] of [[-115, 47], [-114.5, 47.8], [-116, 47.9]]) {
      const cube = box(this.group, x, .6, z, 1, 1.2, 1, '#f0efda'); cube.rotation.y = .3;
      this.obstacles.push({ x, z, radius: .7 });
    }
    leaf(this.group, -108, .035, 56, 5.6, 1.5, '#b5a666', -1.4);
  }

  heightAt(x: number, z: number): number {
    if (onCanalBridge(x, z)) return .52;
    const fountain = this.corners.heightAt(x, z); if (fountain) return fountain;
    if (picnicReserved(x, z)) return .14;
    const bridge = this.bridge.heightAt(x, z); if (bridge) return bridge;
    const painting = paintingHeight(x, z); if (painting) return painting;
    const dx = x + 66; const dz = z - 29;
    if (Math.abs(dx) < 13 && Math.abs(dz) < 10) return .12 + Math.sin((dx + 13) / 26 * Math.PI) * Math.sin((dz + 10) / 20 * Math.PI) * 1.55;
    return 0;
  }

  private napkin(): void {
    const cloth = texture('cloth'); cloth.repeat.set(5, 4);
    const geo = new THREE.PlaneGeometry(26, 20, 26, 20); geo.rotateX(-Math.PI / 2);
    const positions = geo.getAttribute('position');
    for (let i = 0; i < positions.count; i++) positions.setY(i, this.heightAt(positions.getX(i) - 66, positions.getZ(i) + 29));
    geo.computeVertexNormals();
    const fabric = mesh(geo, new THREE.MeshStandardMaterial({ map: cloth, side: THREE.DoubleSide, roughness: 1 }), this.group, -66, .015, 29); fabric.castShadow = false;
    for (let i = 0; i < 30; i++) tube(this.group, [new THREE.Vector3(-79 + i * .87, .12, 39), new THREE.Vector3(-79.1 + i * .87, .05, 39.5)], .025, '#e8dfc9');
    // A folded ridge creates an optional nook along the northern edge.
    const fold = box(this.group, -68, .6, 20, 7, .1, 2.3, '#e4d8c8'); fold.rotation.x = -.45;
  }

  private lake(): void {
    LOTUS_LANDINGS.forEach((p, i) => {
      const shape = new THREE.Shape(); shape.moveTo(0, 0); shape.absarc(0, 0, 1.5, .25, Math.PI * 2 - .25, false); shape.lineTo(0, 0);
      const pad = mesh(new THREE.ShapeGeometry(shape), '#648b53', this.group, p.x, .15, p.z); pad.rotation.x = -Math.PI / 2;
      if (i === 2) {
        for (let j = 0; j < 8; j++) { const angle = j * Math.PI / 4; const petal = ball(this.group, p.x + Math.cos(angle) * .55, .5, p.z + Math.sin(angle) * .55, .3, .35, .65, j % 2 ? '#efb8cb' : '#f4d3df'); petal.rotation.y = Math.PI / 2 - angle; }
        ball(this.group, p.x, .55, p.z, .3, .23, .3, '#e4b956');
      }
    });
    const basin = mesh(new THREE.CircleGeometry(12, 64), '#7aaca2', this.group, -30, .04, 25); basin.rotation.x = -Math.PI / 2; basin.scale.set(1.35, .9, 1); basin.castShadow = false;
    const surface = mesh(new THREE.CircleGeometry(11.7, 64), new THREE.MeshStandardMaterial({ color: '#61aeb0', metalness: .35, roughness: .2, transparent: true, opacity: .84 }), this.group, -30, .07, 25); surface.rotation.x = -Math.PI / 2; surface.scale.set(1.35, .9, 1); surface.castShadow = false; this.water.push(surface);
    for (let i = 0; i < 3; i++) { const ring = mesh(new THREE.RingGeometry(2.2 + i * 2, 2.24 + i * 2, 64), new THREE.MeshBasicMaterial({ color: '#d7eddb', transparent: true, opacity: .3, side: THREE.DoubleSide }), this.group, -32, .09, 26); ring.rotation.x = -Math.PI / 2; ring.castShadow = false; this.water.push(ring); }
    tube(this.group, [new THREE.Vector3(-51, .02, 59), new THREE.Vector3(-47, .025, 56), new THREE.Vector3(-44, .02, 55), new THREE.Vector3(-38, .02, 51)], .15, '#62654f');
    for (let i = 0; i < 4; i++) ball(this.group, -49 + i * 3, .08, 57 - i * 1.5, .6, .12, .3, '#656c55');
  }

  private garden(): void {
    planter(this.group, 9, -23, 1.2); this.obstacles.push({ x: 9, z: -23, radius: 7 });
    for (let i = 0; i < 5; i++) {
      const x = -4 + i * 5;
      tube(this.group, [new THREE.Vector3(9, .1, -19), new THREE.Vector3(x, .5, -13), new THREE.Vector3(x - 2, .12, -6 + i)], .32, '#896a43');
    }
    for (let i = 0; i < 8; i++) leaf(this.group, -9 + i * 4, .04, -9 - (i % 3) * 5, 4.5, 1.2, i % 2 ? '#7c984d' : '#54805c', i);
  }

  private bench(): void {
    for (let i = 0; i < 6; i++) {
      const seat = box(this.group, 51, 13, -24 + i * 1.5, 27, .8, 1.2, i % 2 ? '#aa8461' : '#b8966a');
      seat.layers.set(1); seat.castShadow = false;
    }
    for (const x of [40, 62]) for (const z of [-23, -16]) {
      box(this.group, x, 6.5, z, 1, 13, 1, '#385f55'); this.obstacles.push({ x, z, radius: .9 });
    }
    for (let i = 0; i < 3; i++) box(this.group, 51, 15 + i * 2, -25, 27, 1.4, .7, '#a7825c');
    for (const x of [40, 62]) { box(this.group, x, 10.5, -25, .8, 21, .8, '#385f55'); this.obstacles.push({ x, z: -25, radius: .7 }); }
    leaf(this.group, 58, .04, -7, 6, 1.4, '#b9a96d', .7);
  }

  private louvre(): void {
    const ground = box(this.group, 110, .025, -32, 55, .05, 58, '#d0d1bd'); ground.castShadow = false;
    // Handcrafted glass pyramid with visible triangular steelwork.
    const pyramid = mesh(new THREE.ConeGeometry(13, 13, 4), new THREE.MeshStandardMaterial({ color: '#91c9c4', metalness: .25, roughness: .22, transparent: true, opacity: .7, side: THREE.DoubleSide }), this.group, 112, 6.5, -54); pyramid.rotation.y = Math.PI / 4;
    const corners = [new THREE.Vector3(-9.2, 0, -9.2), new THREE.Vector3(9.2, 0, -9.2), new THREE.Vector3(9.2, 0, 9.2), new THREE.Vector3(-9.2, 0, 9.2)];
    const structure = new THREE.Group(); structure.position.set(112, .1, -54); this.group.add(structure);
    for (let side = 0; side < 4; side++) {
      const a = corners[side]; const b = corners[(side + 1) % 4]; const top = new THREE.Vector3(0, 13, 0);
      tube(structure, [a, top], .08, '#e3f1dc');
      for (let k = 1; k < 7; k++) {
        tube(structure, [a.clone().lerp(top, k / 7), b.clone().lerp(top, k / 7)], .035, '#d4e5d2');
        tube(structure, [a.clone().lerp(b, k / 7), top], .035, '#d4e5d2');
      }
    }
    this.obstacles.push({ x: 112, z: -54, radius: 12 });
    const palace = new THREE.Group(); palace.position.set(109, 0, -79); this.group.add(palace);
    box(palace, 0, 8, 0, 66, 16, 8, '#d9cfb3');
    box(palace, 0, 16.5, 0, 68, 1.2, 9, '#ede2c8');
    box(palace, 0, 9, 4.2, 67, .6, .5, '#f0e6cb');
    const roofShape = new THREE.Shape(); roofShape.moveTo(-4.5, 17.1); roofShape.lineTo(0, 22); roofShape.lineTo(4.5, 17.1); roofShape.closePath();
    const roof = mesh(new THREE.ExtrudeGeometry(roofShape, { depth: 66, bevelEnabled: false }), '#66716b', palace, -33, 0, 0); roof.rotation.y = Math.PI / 2;
    for (let i = -7; i <= 7; i++) for (const y of [4, 12]) {
      box(palace, i * 4.1, y, 4.08, 1.7, 3.3, .18, '#536e6c');
      box(palace, i * 4.1, y + 1.9, 4.15, 2.5, .45, .45, '#eee0c1');
      box(palace, i * 4.1, y, 4.22, .14, 3.3, .1, '#c7be9f');
    }
    for (const x of [-29, 0, 29]) { box(palace, x, 10, 2, 8, 20, 9, '#ded4b9'); const r = mesh(new THREE.ConeGeometry(7, 7, 4), '#626e68', palace, x, 23.5, 2); r.rotation.y = Math.PI / 4; }
  }

  private collectibles(): void {
    for (const item of FINDS) {
      const g = new THREE.Group(); g.position.set(item.x, this.heightAt(item.x, item.z), item.z); this.group.add(g); this.pickups.set(item.id, g);
      if (item.kind === 'crumb') {
        const flake = mesh(new THREE.DodecahedronGeometry(.27, 0), '#e9b857', g, 0, .25); flake.scale.set(1.2, .8, .9);
        ball(g, .05, .34, .02, .1, .07, .08, '#fce1a0');
      } else if (item.kind === 'sugar') {
        const crystal = mesh(new THREE.OctahedronGeometry(.38), material('#c3ece1', .25, .15), g, 0, .65); crystal.rotation.z = .2;
      } else {
        mesh(new THREE.CylinderGeometry(.8, .8, .17, 32), '#c65a4c', g, 0, .16);
        for (const x of [-.22, .22]) for (const z of [-.22, .22]) mesh(new THREE.CylinderGeometry(.09, .09, .02, 12), '#672f2b', g, x, .255, z);
      }
      const halo = mesh(new THREE.RingGeometry(.5, .54, 32), new THREE.MeshBasicMaterial({ color: '#fff0a6', transparent: true, opacity: .65, side: THREE.DoubleSide }), g, 0, .055); halo.rotation.x = -Math.PI / 2; halo.castShadow = false;
    }
  }

  private markers(): void {
    [...PLACES, ...REGIONS].forEach((p, i) => {
      const g = new THREE.Group(); g.position.set(p.x, this.heightAt(p.x, p.z), p.z); this.group.add(g); this.beacons.set(p.id, g);
      const station = i > 0 ? SAVE_STATIONS[i - 1] : p;
      const ring = mesh(new THREE.RingGeometry(1.05, 1.12, 48), new THREE.MeshBasicMaterial({ color: '#e6d898', transparent: true, opacity: .75, side: THREE.DoubleSide }), g, station.x - p.x, this.heightAt(station.x, station.z) - g.position.y + .06, station.z - p.z); ring.rotation.x = -Math.PI / 2; ring.castShadow = false;
      if (i > 0) {
        const station = SAVE_STATIONS[i - 1];
        const gem = mesh(new THREE.OctahedronGeometry(.5), '#f0d486', g, station.x - p.x, this.heightAt(station.x, station.z) - g.position.y + 1.9, station.z - p.z); gem.name = 'beacon';
        gem.userData.baseHeight = gem.position.y;
      }
    });
    // A quiet trail of flecks gives direction without drawing a line over the world.
    for (let i = 1; i < PLACES.length; i++) {
      const a = PLACES[i - 1]; const b = PLACES[i];
      for (let t = .12; t < 1; t += .12) {
        const x = THREE.MathUtils.lerp(a.x, b.x, t); const z = THREE.MathUtils.lerp(a.z, b.z, t);
        const fleck = mesh(new THREE.CircleGeometry(.045, 8), '#ddd49c', this.group, x, this.heightAt(x, z) + .03, z); fleck.rotation.x = -Math.PI / 2; fleck.castShadow = false;
      }
    }
  }

  blocked(x: number, z: number): boolean { return !inWorld(x, z) || this.frontier.blocked(x, z) || this.evening.blocked(x, z) || this.corners.blocked(x, z) || this.picnic.blocked(x, z) || this.bridge.blocked(x, z) || this.cat.collides(x, z) || this.obstacles.some(o => Math.hypot(x - o.x, z - o.z) < o.radius + .34) || (x >= 144 && this.outskirts.blocked(x, z)); }

  stream(x: number, z: number): void {
    this.frontier.stream(x, z);
    this.outskirts.stream(x, z);
    const cx = Math.floor(x / 24); const cz = Math.floor(z / 24); const key = `${cx}:${cz}`; if (key === this.lastChunk) return; this.lastChunk = key;
    const keep = new Set<string>();
    for (let dx = -2; dx <= 2; dx++) for (let dz = -2; dz <= 2; dz++) {
      const id = `${cx + dx}:${cz + dz}`; keep.add(id);
      if (!this.details.has(id)) { const detail = this.makeDetail(cx + dx, cz + dz); this.details.set(id, detail); this.group.add(detail); }
    }
    for (const [id, detail] of this.details) if (!keep.has(id)) { this.group.remove(detail); detail.traverse(o => { if (o instanceof THREE.InstancedMesh) o.dispose(); }); this.details.delete(id); }
  }

  private makeDetail(cx: number, cz: number): THREE.Group {
    const g = new THREE.Group(); const rand = random((cx + 30) * 3713 + (cz + 30) * 15377);
    const stones = new THREE.InstancedMesh(this.pebbleGeometry, this.pebbleMaterial, 100);
    const grasses = new THREE.InstancedMesh(this.grassGeometry, this.grassMaterial, 95);
    stones.receiveShadow = true; stones.castShadow = true; grasses.castShadow = true;
    const dummy = new THREE.Object3D(); let count = 0;
    for (let i = 0; i < 100; i++) {
      const x = cx * 24 + rand() * 24; const z = cz * 24 + rand() * 24; const s = .025 + rand() * .12;
      if (x >= 144 && x <= 528 && Math.abs(z + 120) < 7) {
        dummy.scale.setScalar(0); dummy.updateMatrix(); stones.setMatrixAt(i, dummy.matrix); continue;
      }
      dummy.position.set(x, this.heightAt(x, z) + s * .25, z); dummy.scale.set(s * 1.3, s * .5, s); if (nearPainting(x, z) || LeafBridge.reserved(x, z) || picnicReserved(x, z) || cornersReserved(x, z) || eveningReserved(x, z)) dummy.scale.setScalar(0); dummy.rotation.set(rand(), rand() * 6, rand()); dummy.updateMatrix(); stones.setMatrixAt(i, dummy.matrix);
      const onSeam = Math.abs(x % 8.33) < .8 || Math.abs(z % 8.5) < .65;
      const nearTrail = PLACES.some(p => Math.hypot(x - p.x, z - p.z) < 2.5);
      if (onSeam && !nearTrail && !nearPainting(x, z) && !cornersReserved(x, z) && !eveningReserved(x, z) && !LeafBridge.reserved(x, z) && !this.blocked(x, z) && this.heightAt(x, z) === 0 && count < 95) {
        dummy.position.set(x, .01, z); dummy.scale.setScalar(.6 + rand() * .9); dummy.rotation.set(0, rand() * 6.28, (rand() - .5) * .25); dummy.updateMatrix(); grasses.setMatrixAt(count++, dummy.matrix);
      }
    }
    grasses.count = count; g.add(stones, grasses); return g;
  }

  animate(time: number, x: number, z: number, allowVisit = true): void {
    this.frontier.animals.animate(time, x, z);
    this.evening.animate(time, x, z);
    this.corners.animate(time, x, z);
    this.picnic.animate(time); this.picnic.group.visible = Math.hypot(x - 264, z - 72) < 80;
    this.friends.update(time, x, z);
    this.cat.animate(time, x, z, allowVisit);
    this.water.forEach((w, i) => { if (i > 0) { const s = 1 + Math.sin(time * .8 + i) * .08; w.scale.set(s, s, 1); } });
    this.pickups.forEach((g, id) => { if (id.startsWith('sugar')) { g.children[0].rotation.y = time * .7; g.children[0].position.y = .7 + Math.sin(time * 2) * .1; } });
    this.beacons.forEach(g => { const b = g.getObjectByName('beacon'); if (b) { b.rotation.y = time; b.position.y = b.userData.baseHeight + Math.sin(time * 1.8) * .15; } });
    this.butterfly.position.set(x + Math.cos(time * .22) * 8, 3 + Math.sin(time) * .6, z - 7 + Math.sin(time * .3) * 4);
    this.butterfly.rotation.y = -time * .3;
    this.butterfly.children.slice(0, 2).forEach((wing, i) => { wing.rotation.z = Math.sin(time * 13) * .7 * (i ? 1 : -1); });
  }
}
